import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Hero from './Hero';
import BlogSection from './BlogSection';
import PricingTiersPicker from './PricingTiersPicker';
import { usePayment } from '../contexts/PaymentContext';
import { PLAN_ORDER, UPGRADE_PLAN_ORDER } from '../lib/plans';

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { initiatePayment, hasUsedTrial, accessTier } = usePayment();

  // Hide the £1 intro for users who've already redeemed it.
  const landingPlans = hasUsedTrial && accessTier !== 'trial' ? UPGRADE_PLAN_ORDER : PLAN_ORDER;

  const handleLandingPlanSelect = async (planId) => {
    if (!isAuthenticated) {
      navigate(`/auth?redirect=/pricing`);
      return;
    }
    await initiatePayment(planId, 'landing-pricing');
  };

  // If user was mid-flow on /claude-code before Stripe redirected them here, hop back
  useEffect(() => {
    try {
      if (localStorage.getItem('claude_landing_return') === '1') {
        localStorage.removeItem('claude_landing_return');
        navigate('/claude-code', { replace: true });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavigateToFeature = (feature) => {
    if (feature === 'create-project') {
      // Check authentication before allowing project creation
      if (isAuthenticated) {
        navigate('/create-project');
      } else {
        navigate('/auth?redirect=/create-project');
      }
    } else if (feature === 'design-inspiration') {
      navigate('/design-inspiration');
    } else if (feature === 'cursor-tips') {
      navigate('/cursor-tips');
    } else if (feature === 'files-guidance') {
      navigate('/files-guidance');
    }
  };

  const handleNavigateToDashboard = () => {
    // Check authentication before allowing dashboard access
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/auth?redirect=/dashboard');
    }
  };
  const handleNavigateToDownloadablePrompts = () => {
    navigate('/downloadable-prompts');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header 
        onNavigateToDashboard={handleNavigateToDashboard}
        onNavigateToDesignInspiration={() => navigate('/design-inspiration')}
        onNavigateToHome={() => navigate('/')}
        onNavigateToProfile={() => navigate('/profile')}
        onNavigateToDownloadablePrompts={handleNavigateToDownloadablePrompts}
      />
      <main className="max-w-6xl mx-auto px-6 py-12 pt-24">
        <Hero onNavigateToFeature={handleNavigateToFeature} />
        <BlogSection />
      </main>

      {/* ==================== PRICING TIERS ==================== */}
      <section className="border-t border-gray-200 bg-gradient-to-b from-white to-gray-50 py-16 sm:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <span className="text-xs font-bold tracking-wider uppercase text-vibe-cyan bg-vibe-cyan/10 py-1 px-3 rounded-full">Pricing</span>
            <h2 className="font-jersey text-3xl sm:text-4xl md:text-5xl text-black mt-4 mb-2">
              Pick a plan that fits
            </h2>
            <p className="text-black/60 text-base sm:text-lg max-w-2xl mx-auto">
              Start with £1 for your first month, go unlimited monthly, or own it for life.
            </p>
          </div>

          <PricingTiersPicker
            plans={landingPlans}
            context="landing-pricing"
            onSelect={handleLandingPlanSelect}
          />

          {hasUsedTrial && accessTier !== 'trial' && (
            <p className="text-center text-xs text-black/40 mt-4">
              You've already used your £1 intro — pick Pro or Lifetime to continue.
            </p>
          )}
        </div>
      </section>

      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          {/* Secondary nav — links demoted from the primary header */}
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-500 mb-6">
            <button
              onClick={() => navigate('/pricing')}
              className="hover:text-gray-900 transition-colors"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/design-inspiration')}
              className="hover:text-gray-900 transition-colors"
            >
              Design Inspiration
            </button>
            <button
              onClick={() => navigate('/downloadable-prompts')}
              className="hover:text-gray-900 transition-colors"
            >
              Downloadable Prompts
            </button>
            <button
              onClick={() => navigate('/cursor-tips')}
              className="hover:text-gray-900 transition-colors"
            >
              Cursor Tips
            </button>
            <button
              onClick={() => navigate('/claude-code')}
              className="hover:text-gray-900 transition-colors"
            >
              Claude Code
            </button>
          </nav>
          <div className="text-center text-sm text-gray-600">
            <span>Contact us: </span>
            <a href="mailto:m.sayed@valyfy.com" className="underline hover:text-black">m.sayed@valyfy.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage; 